/**
 * useTravelerReview Hook
 * Main hook for traveler review page - composes smaller focused hooks
 */

import { useRef, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { medicationService } from "@/services/medications";
import { useFormData } from "./useFormData";
import { useDestinationInfo } from "./useDestinationInfo";
import { useReviewActions } from "./useReviewActions";
import { useMedicationSelections } from "./useMedicationSelections";

export function useTravelerReview() {
  const { travelerId, action } = useParams<{
    travelerId: string;
    action?: string;
  }>();

  const printRef = useRef<HTMLDivElement>(null);
  const isInIframe = typeof window !== "undefined" && window.self !== window.top;
  const isViewMode = action === "view";
  const hasFetchedRef = useRef(false);

  // Destination info hook
  const {
    destinationInfoMap,
    loadingDestinations,
    expandedDestinations,
    fetchDestinationInfo,
    toggleDestination,
  } = useDestinationInfo();

  // Review actions hook (modals, alerts, approval flow)
  const reviewActions = useReviewActions({
    travelerId,
    isInIframe,
  });

  const { showAlert } = reviewActions;

  // Medication selections hook
  const medicationSelections = useMedicationSelections({
    travelerId,
    showAlert,
  });

  // Fetch AI recommendations
  const fetchRecommendations = useCallback(async (id: string) => {
    try {
      const recommendations = await medicationService.getAIRecommendations(id, true);
      medicationSelections.initializeFromRecommendations(recommendations);
    } catch (error) {
      // Silent fail - AI recommendations not critical for form display
    }
  }, [medicationSelections.initializeFromRecommendations]);

  // Form data hook
  const {
    formData,
    loading,
    error,
    fetchTravelerForm,
  } = useFormData({
    travelerId,
    isViewMode,
    onDestinationsLoaded: fetchDestinationInfo,
    onRecommendationsNeeded: fetchRecommendations,
  });

  // Fetch form on mount
  useEffect(() => {
    if (travelerId && !hasFetchedRef.current) {
      hasFetchedRef.current = true;
      fetchTravelerForm();
    }
  }, [travelerId, fetchTravelerForm]);

  return {
    // Form data
    formData,
    loading,
    error,

    // Destination info
    destinationInfoMap,
    loadingDestinations,
    expandedDestinations,
    toggleDestination,

    // Modal state
    flagReason: reviewActions.flagReason,
    setFlagReason: reviewActions.setFlagReason,
    noteContent: reviewActions.noteContent,
    setNoteContent: reviewActions.setNoteContent,
    showFlagModal: reviewActions.showFlagModal,
    setShowFlagModal: reviewActions.setShowFlagModal,
    showNoteModal: reviewActions.showNoteModal,
    setShowNoteModal: reviewActions.setShowNoteModal,
    showConfirmModal: reviewActions.showConfirmModal,
    setShowConfirmModal: reviewActions.setShowConfirmModal,
    processing: reviewActions.processing,
    expandedSections: reviewActions.expandedSections,
    showQuickNote: reviewActions.showQuickNote,
    setShowQuickNote: reviewActions.setShowQuickNote,
    quickNoteContent: reviewActions.quickNoteContent,
    setQuickNoteContent: reviewActions.setQuickNoteContent,

    // Alert state
    alertState: reviewActions.alertState,
    setAlertState: reviewActions.setAlertState,
    showAlert: reviewActions.showAlert,

    // Actions
    handleApprove: reviewActions.handleApprove,
    confirmApprove: reviewActions.confirmApprove,
    handleFlag: reviewActions.handleFlag,
    handleAddNote: reviewActions.handleAddNote,
    handleQuickNote: reviewActions.handleQuickNote,
    handleBackNavigation: reviewActions.handleBackNavigation,
    toggleSection: reviewActions.toggleSection,

    // Medication selections
    medicationSelections: medicationSelections.medicationSelections,
    medicationNotes: medicationSelections.medicationNotes,
    refillInfos: medicationSelections.refillInfos,
    administrationSites: medicationSelections.administrationSites,
    quantities: medicationSelections.quantities,
    aiRecommendations: medicationSelections.aiRecommendations,
    savingSelections: medicationSelections.savingSelections,
    selectionVersion: medicationSelections.selectionVersion,
    handleMedicationSelectionChange: medicationSelections.handleMedicationSelectionChange,
    handleMedicationNoteChange: medicationSelections.handleMedicationNoteChange,
    handleRefillInfoChange: medicationSelections.handleRefillInfoChange,
    handleAdministrationSiteChange: medicationSelections.handleAdministrationSiteChange,
    handleQuantityChange: medicationSelections.handleQuantityChange,
    saveItemNote: medicationSelections.saveItemNote,
    saveItemDetails: medicationSelections.saveItemDetails,
    saveSelections: medicationSelections.saveSelections,
    setAiRecommendations: medicationSelections.setAiRecommendations,
    pharmacistRecommendationNote: medicationSelections.pharmacistRecommendationNote,
    handleRecommendationNoteChange: medicationSelections.handleRecommendationNoteChange,

    // Refs and computed
    printRef,
    isInIframe,
    isViewMode,
    fetchTravelerForm,
  };
}
